import React, { useMemo, useCallback, useState } from "react";
import { UI_CAPTION, colors2 } from "oolib";
import { StyledBlocksWrapper, StyledOrConnector } from "../AppliedFilters/styled";
import { AppliedFilterBlock } from "../AppliedFilters/AppliedFilterBlock";
import { groupDocFilters } from "./utils/groupDocFilters";
import { FilterLightbox } from "../AppliedFilters/FilterLightbox";

// Session-scoped flags for the first-use guidance coachmarks.
const MULTI_DATASET_GUIDANCE_KEY = "ooloi_docFilter_multiDataset_guidance_seen_v1";
const INDIRECT_FILTER_GUIDANCE_KEY = "ooloi_docFilter_indirectFilter_guidance_seen_v1";

// Rollup ("indirect") filters carry one of these filterTypes.
const ROLLUP_FILTER_TYPES = new Set(["nestedRollupTagType", "rollupValuePathType"]);
const isIndirectBlock = (b) =>
  ROLLUP_FILTER_TYPES.has(b.filterConfig?.target?.filterType) ||
  ROLLUP_FILTER_TYPES.has(b.filterConfig?.source?.filterType);

export const DocAppliedFiltersBlocks = ({
  activeFilters,
  onChange,
  docFilterConfig,
  savedConfigs = [],
  onIntraFilterOperatorChange,
  onRemoveSavedConfig,
  contentTypes,
  disableInlinePadding,
  noAppliedValueChipsTruncation = false,
  // How the first-use guidance coachmarks (multi-dataset + indirect-filter) behave.
  // "oncePerSession" (default) auto-opens once per session; "always" re-shows every
  // time; "never" disables the coachmark (filters still get plain hover tooltips).
  guidanceMode = "oncePerSession",
}) => {
  const blocks = useMemo(
    () => groupDocFilters(activeFilters, docFilterConfig, savedConfigs, contentTypes?.length ?? Infinity),
    [activeFilters, docFilterConfig, savedConfigs, contentTypes]
  );

  // First-use guidance coachmarks: shown once per session (per mode) on the FIRST
  // multi-dataset filter and the FIRST indirect filter. After acknowledgement the
  // block falls back to a plain hover tooltip.
  const readSeen = useCallback(
    (key) => {
      if (guidanceMode === "always") return false; // never "seen" → always coachmark
      if (guidanceMode === "never") return true; //  always "seen" → never coachmark
      try {
        return sessionStorage.getItem(key) === "1";
      } catch (e) {
        return false;
      }
    },
    [guidanceMode]
  );
  const [datasetSeen, setDatasetSeen] = useState(() => readSeen(MULTI_DATASET_GUIDANCE_KEY));
  const [indirectSeen, setIndirectSeen] = useState(() => readSeen(INDIRECT_FILTER_GUIDANCE_KEY));

  const makeDismiss = useCallback(
    (key, setSeen) => () => {
      if (guidanceMode !== "oncePerSession") return; // "always"/"never" don't persist
      try {
        sessionStorage.setItem(key, "1");
      } catch (e) {
        /* sessionStorage unavailable — guidance just won't persist */
      }
      setSeen(true);
    },
    [guidanceMode]
  );
  const dismissDataset = useMemo(
    () => makeDismiss(MULTI_DATASET_GUIDANCE_KEY, setDatasetSeen),
    [makeDismiss]
  );
  const dismissIndirect = useMemo(
    () => makeDismiss(INDIRECT_FILTER_GUIDANCE_KEY, setIndirectSeen),
    [makeDismiss]
  );

  const firstMultiDatasetKey = useMemo(
    () => blocks.find((b) => b.breadcrumbTooltip)?.key,
    [blocks]
  );
  const firstIndirectKey = useMemo(
    () => blocks.find(isIndirectBlock)?.key,
    [blocks]
  );

  const renderLightbox = useCallback(
    ({ block, activeFilters, onChange, onOpenChange, children }) => (
      <FilterLightbox
        block={block}
        activeFilters={activeFilters}
        onChange={onChange}
        onOpenChange={onOpenChange}
        contentTypes={contentTypes}
      >
        {children}
      </FilterLightbox>
    ),
    [contentTypes]
  );

  if (!blocks.length) return null;

  return (
    <StyledBlocksWrapper $disableInlinePadding={disableInlinePadding}>
      {blocks.map((block) => (
        <React.Fragment key={block.key}>
          <AppliedFilterBlock
            block={block}
            activeFilters={activeFilters}
            onChange={onChange}
            renderLightbox={renderLightbox}
            intraFilterOperator={activeFilters.find((f) => f.filterId === block.filterId)?.intraFilterOperator || "OR"}
            onIntraFilterOperatorChange={onIntraFilterOperatorChange}
            onRemoveSavedConfig={onRemoveSavedConfig}
            contentTypes={contentTypes}
            noAppliedValueChipsTruncation={noAppliedValueChipsTruncation}
            showDatasetGuidance={!datasetSeen && block.key === firstMultiDatasetKey}
            onDismissDatasetGuidance={dismissDataset}
            showIndirectGuidance={!indirectSeen && block.key === firstIndirectKey}
            onDismissIndirectGuidance={dismissIndirect}
          />
          {/* OR-siblings render adjacently (see clusterBlocksByOrGroup); an "OR"
              connector between each pair makes the OR relationship explicit. */}
          {block.orGroupSize > 1 && block.orGroupPos < block.orGroupSize - 1 && (
            <StyledOrConnector>
              <UI_CAPTION color={colors2.grey50}>OR</UI_CAPTION>
            </StyledOrConnector>
          )}
        </React.Fragment>
      ))}
    </StyledBlocksWrapper>
  );
};

export { isDocFilter, groupDocFilters } from "./utils/groupDocFilters";
